import { cn } from "@/utils/cn";
import { heatColor, heatColorGray } from "./profileVector";

const FALLBACK_DIMS = 111;

type HeatmapRowProps = {
  name: string;
  family: string | null;
  vector: number[];
  /** "substance" rows show a thicker, two-tone family stripe like the
   *  Flask rows do; "compound" rows get a single-tone stripe. */
  kind?: "compound" | "substance";
  /** Dimmed for foundation/fixed-ingredient rows so they read as locked. */
  dim?: boolean;
  /** Pulses a green left edge for one cycle — used on the row the AI is
   *  currently mutating, so the eye lands on it without scrolling. */
  pulse?: boolean;
  height?: number;
  tone?: "color" | "gray";
};

export function HeatmapRow({
  name,
  family,
  vector,
  kind = "compound",
  dim = false,
  pulse = false,
  height = 30,
  tone = "color",
}: HeatmapRowProps) {
  // Real profile vectors land with the pin's embedding_dim (typically 111
  // today; other dims valid). Empty (skipped compound) → render the
  // FALLBACK_DIMS-wide empty grid so the row scaffolding still has the
  // right horizontal weight in the heatmap stack.
  const cells = vector.length > 0 ? vector : new Array<number>(FALLBACK_DIMS).fill(0);
  const colorFn = tone === "gray" ? heatColorGray : heatColor;
  const fam = family || "neutral";
  return (
    <div
      className={cn("af-heatrow", pulse && "af-pulse")}
      style={{
        height,
        opacity: dim ? 0.5 : 1,
      }}
    >
      {/* Family stripe — single bar for compounds, two-tone for substances */}
      <div
        className="af-heatrow-stripe"
        style={{
          width: kind === "substance" ? 6 : 3,
          filter: tone === "gray" ? "grayscale(1) opacity(0.55)" : undefined,
        }}
      >
        <div style={{ background: `var(--fam-${fam}, var(--fam-default))` }} />
        {kind === "substance" && (
          <div style={{ background: `var(--fam-${fam}, var(--fam-default))`, opacity: 0.5 }} />
        )}
      </div>
      {/* N-cell vector grid. CSS defaults to 111 columns; we override when
          the real embedding_dim differs so the grid still spans the row. */}
      <div
        className="af-vec"
        style={{
          paddingLeft: kind === "substance" ? 6 : 3,
          gridTemplateColumns: `repeat(${cells.length}, 1fr)`,
        }}
      >
        {cells.map((v, i) => (
          <div key={i} style={{ background: colorFn(v) }} />
        ))}
      </div>
      {/* Translucent name overlay so the underlying vector still reads through */}
      <div className="af-heatrow-label">
        <span
          style={{
            fontStyle: kind === "substance" ? "italic" : "normal",
            fontFamily: kind === "substance" ? "var(--font-display)" : "inherit",
          }}
        >
          {name}
        </span>
      </div>
    </div>
  );
}
